// =============================================================================
// ui/tabs/DefaultRiskEditor.tsx — Settings → Default Risk Per Instrument.
// -----------------------------------------------------------------------------
// ── 🚨 THIS SETTING WAS ALREADY DRIVING THE APP, SILENTLY ──
//
// The trade form has read it since it shipped: with no stored risk plan, Risk
// and R:R are filled from here, and Realized R is `points ÷ risk` — so an
// unconfigured instrument blanks BOTH R columns on every trade. Josh's
// screenshot of beta showed `Planned R 2.00 / Realized R 3.42` where the port
// showed em dashes on the same trade. The difference was this map, which the
// port could read and had no way to edit.
//
// A setting that is read and not writable is worse than one that is missing:
// the effect is visible and the cause is not.
//
// ── THE SHAPE, TRANSCRIBED FROM THE OLD CLIENT'S MARKUP ──
//
// A table of configured instruments (stop, R:R, the implied risk in dollars, a
// remove button), then a row to add one from the catalogue, then Clear All.
//
// ⚠ EDITS COMMIT ON BLUR, not per keystroke — `onChange` in the old client is
// the DOM change event, which fires on blur. Saving per keystroke would issue a
// write for every digit of "12.5".
// =============================================================================

import { useState } from 'react';
import { useContracts } from '../state/contracts';
// ⚠ `moneyPlain`, NOT `money`. The signed formatter renders a positive as
// "+$20.00", and this column is what a trade RISKS — a cost, never a gain. The
// old client uses its plain variant here for the same reason.
import { moneyPlain } from '../format';
import {
  addRiskInstrument, asRiskMap, removeRiskInstrument, riskDollars, riskSymbols,
  setRiskField, unconfiguredSymbols,
  type DefaultRiskField, type DefaultRiskMap,
} from '../../selectors/defaultRisk';

export interface DefaultRiskEditorProps {
  /** The stored map, straight off preferences. */
  value: Record<string, unknown>;
  /** ⚠ The WHOLE map, as one field patch. See the note in `commit`. */
  onChange: (next: DefaultRiskMap) => void;
  busy?: boolean;
}

export function DefaultRiskEditor({ value, onChange, busy }: DefaultRiskEditorProps) {
  // ⚠ `contracts` is NULL until the catalogue read SUCCEEDS, and that is not the
  // same as an empty catalogue — see the note in `state/contracts.tsx`. The
  // difference matters here: with a failed read, `unconfiguredSymbols` returns
  // nothing, and rendering the usual "(all instruments configured)" would state
  // as fact something that is merely unknown. Distinguished below.
  const { contracts } = useContracts();
  const catalogueUnavailable = contracts === null;
  const [addSym, setAddSym] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  // ⚠ DERIVED, never copied into state (D86). This map arrives from an async
  // preferences read; seeding local state from it would render the empty
  // default and never catch up.
  const map = asRiskMap(value);
  const symbols = riskSymbols(map);
  const available = unconfiguredSymbols(map, contracts);

  /**
   * What the dropdown is actually offering — the trader's pick while it is
   * still on the list, otherwise the first instrument available.
   *
   * ⚠ DERIVED for the same reason the map above is (D86). The catalogue arrives
   * asynchronously, so at first paint `available` is empty; a default seeded
   * into state at mount would be `''` forever and the control would open on a
   * placeholder that beta does not have. It also self-corrects when the pick is
   * added and leaves the list, with no effect to keep the two in step.
   */
  const selected = available.includes(addSym) ? addSym : (available[0] ?? '');

  /**
   * `SYM — Full Name`, which is how beta labels these (index.html:50108).
   *
   * ⚠ A LOOKUP, not a second source of truth for WHICH instruments are offered:
   * `unconfiguredSymbols` decides that and the order, and this only puts a name
   * beside a symbol it already chose. A contract whose name is blank falls back
   * to the bare symbol rather than rendering a dangling dash.
   */
  const labelFor = (sym: string) => {
    const name = contracts?.find((c) => c.symbol === sym)?.name?.trim();
    return name ? `${sym} — ${name}` : sym;
  };

  const now = () => new Date().toISOString();

  /**
   * ⚠ The whole map goes back, because the map IS the field. `usePreferences`
   * patches one column per call, and this column's value is the entire object —
   * there is no partial write of a JSON column that would leave the rest alone.
   * The domain helpers are what guarantee the untouched entries travel through
   * unchanged.
   */
  const commit = (next: DefaultRiskMap) => onChange(next);

  const setField = (sym: string, field: DefaultRiskField) =>
    (e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) =>
      commit(setRiskField(map, sym, field, e.target.value, now()));

  return (
    <section className="card dri-card">
      <h3>Default Risk Per Instrument</h3>
      <p className="muted dri-blurb">
        Your typical stop and R:R per instrument, so the trade form prefills
        sensibly. <b>Always overridable per trade.</b> Imported trades without a
        risk plan get your default; existing values are never overwritten.
      </p>

      {symbols.length === 0 ? (
        <p className="muted dri-empty">
          No instruments configured. Pick one below to add your first default.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="table dri-table">
            <thead>
              <tr>
                <th>Instrument</th>
                <th className="num">Stop (pts)</th>
                <th className="num">R:R Planned</th>
                <th className="num">Risk $ @ 1 contract</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {symbols.map((sym) => {
                const entry = map[sym] ?? {};
                const dpp = (contracts ?? []).find((c) => c.symbol === sym)?.dollarsPerPoint ?? null;
                const dollars = riskDollars(entry, dpp);
                const numOr = (k: string): string => {
                  const v = entry[k];
                  return typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
                };
                return (
                  <tr key={sym}>
                    <td className="dri-sym">{sym}</td>
                    <td className="num">
                      <input
                        type="number" step="any" min={0} disabled={busy}
                        aria-label={`Stop in points for ${sym}`}
                        // ⚠ `defaultValue` + `key`, not `value`. The commit is on
                        // BLUR, so a controlled input would fight every keystroke
                        // against a map that has not been updated yet. The key
                        // re-seeds the box when the stored value actually changes.
                        key={`r-${sym}-${numOr('riskPoints')}`}
                        defaultValue={numOr('riskPoints')}
                        placeholder="e.g. 4"
                        onBlur={setField(sym, 'riskPoints')}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number" step="any" min={0} disabled={busy}
                        aria-label={`R:R planned for ${sym}`}
                        key={`rr-${sym}-${numOr('rrPlanned')}`}
                        defaultValue={numOr('rrPlanned')}
                        placeholder="e.g. 2"
                        onBlur={setField(sym, 'rrPlanned')}
                      />
                    </td>
                    {/* ⚠ An em dash, not $0. "No stop set" and "risks nothing"
                        are opposite statements. */}
                    <td className="num muted dri-dollars">
                      {dollars == null ? '—' : moneyPlain(dollars, 2)}
                    </td>
                    <td className="num">
                      <button
                        type="button" className="btn danger btn-sm" disabled={busy}
                        aria-label={`Remove ${sym}`}
                        onClick={() => commit(removeRiskInstrument(map, sym))}
                      >×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="dri-add">
        <label htmlFor="dri-add-sym" className="muted">Add instrument:</label>
        {/* ⚠ NO "(pick one)" PLACEHOLDER, which beta does not have either: the
            first instrument is already selected and Add is live, so adding one
            is a single press instead of a pick-then-press. The placeholder cost
            a step on the most common path and left Add greyed out, which reads
            as a broken control rather than as a prompt. */}
        <select
          id="dri-add-sym"
          value={selected}
          disabled={busy || !available.length}
          onChange={(e) => setAddSym(e.target.value)}
        >
          {available.length ? (
            available.map((s) => <option key={s} value={s}>{labelFor(s)}</option>)
          ) : (
            <option value="">
              {catalogueUnavailable
                ? '(instrument list unavailable)'
                : '(all instruments configured)'}
            </option>
          )}
        </select>
        {/* ⚠ Disabled on `selected`, so the only thing that greys it out is
            having nothing left to add — beta's `disabled = !available.length`. */}
        <button
          type="button" className="btn"
          disabled={busy || !selected}
          onClick={() => { commit(addRiskInstrument(map, selected, now())); setAddSym(''); }}
        >+ Add</button>

        <span className="grow" />

        {/* ⚠ TWO PRESSES, and the second says what it does. This wipes a
            methodology the user may have built over months, and the old client
            guards it with a confirm dialog. */}
        {confirmClear ? (
          <>
            <span className="muted dri-confirm">
              Clear all {symbols.length}? New trades will land with blank Risk and R:R.
            </span>
            <button type="button" className="btn" disabled={busy}
              onClick={() => setConfirmClear(false)}>Cancel</button>
            <button
              type="button" className="btn danger" disabled={busy}
              onClick={() => { commit({}); setConfirmClear(false); }}
            >Yes, clear all</button>
          </>
        ) : (
          <button
            type="button" className="btn danger"
            disabled={busy || !symbols.length}
            onClick={() => setConfirmClear(true)}
          >Clear All</button>
        )}
      </div>
    </section>
  );
}
